/**
 * The line icons the artboards draw. Round caps, sized by the caller — they
 * inherit `currentColor` so a nav item's active state moves the icon with the
 * label.
 *
 * The stroke is 1px of SCREEN, not 1px of the 24-unit viewBox: `b4-icon` sets
 * `vector-effect: non-scaling-stroke` on the shapes, so the width survives the
 * scale down. It used to be `strokeWidth={1.8}` in viewBox units, which is a
 * different weight at every size the set is drawn at — 1.35px in a TileRow at
 * 18, 1.2px in the menu at 16, 0.9px on the 12px branch glyph — and none of
 * them a whole number of device pixels. A stroke that lands between pixel rows
 * is drawn as two rows at part opacity, which is why the icons read soft and
 * grey next to the text beside them rather than as lines.
 */
const Svg = ({ size = 16, children, className = '' }: {
  size?: number,
  children: React.ReactNode,
  className?: string,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`b4-icon flex-none ${className}`}
    aria-hidden="true"
  >
    {children}
  </svg>
)

type IconProps = { size?: number, className?: string };

const path = (d: string) => ({ size, className }: IconProps) => (
  <Svg size={size} className={className}><path d={d} /></Svg>
);

// The screen it opens, drawn as a screen: one frame, a header strip, two
// panels under it. The four detached squares that stood here were a bento
// grid — a shape the Overview does not have, and at 18px four separate boxes
// with the gaps between them read as noise rather than as a layout.
export const OverviewIcon = path('M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM3 9h18M12 9v12');
// A rocket needs its fins. This was the same drawing minus the two of them,
// which left a body and an exhaust puff floating apart — it read as a comet,
// or a paintbrush, and never as the thing that ships an app.
export const DeploymentsIcon = path('M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09zM12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2zM9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5');
export const LogsIcon = path('M4 6h16M4 12h10M4 18h13');
export const MetricsIcon = path('M3 17l6-6 4 4 8-8');
// A key. The stepped rules that stood here were the Logs icon with different
// lengths — three horizontal lines is three horizontal lines, and the two sit
// four rows apart in the same menu. Braces were the other candidate and read
// as code rather than as the thing; a key is what the section holds.
// The `h.01` at the end is the bit: a zero-length round-capped stroke, which
// is how you get a dot out of a helper that only draws one path.
export const VariablesIcon = path('M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4zM16.5 7.5h.01');
// The cog, not the eight-rayed thing that stood here and read as a sun. Same
// silhouette as the `menu-settings.svg` this menu used before the restyle,
// redrawn as a stroke so it carries the same weight as its five neighbours
// instead of the mask-filled 26x26 the asset is.
export const SettingsIcon = ({ size, className }: IconProps) => (
  <Svg size={size} className={className}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </Svg>
);

export const FileIcon = path('M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6');
// The build's working directory. Deliberately a folder and not a second
// document outline — Source above it already carries FileIcon, and the two
// rows sat close enough that a near-identical glyph read as a repeat.
export const FolderIcon = path('M3 7a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.6.8l1 1.2H19a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z');
// The socket the app answers on. The stacked-disc cylinder that stood here is
// the universal symbol for a database — on a row that names a TCP port, next
// to rows about builds and folders, it read as "this app has a database".
export const PortIcon = path('M9 8V3M15 8V3M6 8h12v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4zM12 17v4');

// The start command: a shell prompt, because that is what the field holds.
export const TerminalIcon = path('M4 17l6-6-6-6M12 19h8');

// Health check: the trace a monitor draws. Not the monitor itself — a frame
// with a line inside it loses the line at 18px, and the line is the meaning.
export const HeartbeatIcon = path('M21 12h-3.5l-2.5 8L9 4l-2.5 8H3');

// What a build produces. The Deployments rocket used to stand on the build
// row too; now that it is unmistakably a rocket, it belongs to the deploy and
// not to the thing being deployed.
export const BoxIcon = path('M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16zM3.3 7L12 12l8.7-5M12 22V12');
// The plan: a card, because what the section changes is what gets billed.
// A cylinder, drawn as the stack it is: the top ellipse plus two rules for the
// two seams below it. Three discs one under the other is what every database
// glyph in the product means, and the section it heads is a list of tables.
export const DatabaseIcon = path('M12 3c4.97 0 9 1.34 9 3s-4.03 3-9 3-9-1.34-9-3 4.03-3 9-3zM3 6v12c0 1.66 4.03 3 9 3s9-1.34 9-3V6M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3');
export const CardIcon = path('M2 7a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2zM2 10h20M6 15h4');
export const GlobeIcon = path('M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM2 12h20M12 2a15 15 0 0 1 0 20a15 15 0 0 1 0-20');
export const BranchIcon = path('M6 3v12M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM18 9a9 9 0 0 1-9 9');

export const PlusIcon = path('M12 5v14M5 12h14');
export const CloseIcon = path('M18 6L6 18M6 6l12 12');
export const TrashIcon = path('M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6');
export const CopyIcon = path('M9 9a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-8a2 2 0 0 1-2-2zM5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1');
/**
 * The "everything else" menu. Filled dots rather than stroked ones: at 1px of
 * stroke a `v.01` dot is a smudge.
 */
export const MoreIcon = ({ size = 16, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={`b4-icon flex-none ${className || ''}`}
    aria-hidden="true"
  >
    <circle cx="12" cy="5" r="1.6" />
    <circle cx="12" cy="12" r="1.6" />
    <circle cx="12" cy="19" r="1.6" />
  </svg>
);
export const CheckIcon = ({ size, className }: IconProps) => (
  <svg width={size ?? 16} height={size ?? 16} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" className={`flex-none ${className ?? ''}`} aria-hidden="true">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);
export const ChevronRightIcon = path('M9 18l6-6-6-6');
export const ChevronDownIcon = path('M6 9l6 6 6-6');
export const ExpandIcon = path('M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7');

// Two arrows chasing each other round a circle, drawn open at the ends so the
// heads have somewhere to sit. A closed ring with heads on it reads as a
// clock.
export const RefreshIcon = path('M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6');
// Down into a tray: the arrow is what says "out of here", the tray is what
// says it lands somewhere.
export const DownloadIcon = path('M12 3v12M7 10l5 5 5-5M4 20h16');
// A panel with its left column marked, which is the thing the button hides
// and shows.
export const PanelLeftIcon = path('M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM9 3v18');
// Rows of a schema rather than rows of data: three lines, the first one short
// the way a column name is short beside its type.
export const ColumnsIcon = path('M4 6h7M4 12h16M4 18h11');

export const ArrowLeftIcon = ({ size, className }: IconProps) => (
  <Svg size={size} className={className}>
    <path d="M19 12H5" />
    <path d="M12 19l-7-7 7-7" />
  </Svg>
);

export const ExternalLinkIcon = ({ size, className }: IconProps) => (
  <Svg size={size} className={className}>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <path d="M15 3h6v6M10 14L21 3" />
  </Svg>
);

export const SearchIcon = ({ size, className }: IconProps) => (
  <Svg size={size} className={className}>
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" />
  </Svg>
);

export const AlertIcon = ({ size, className }: IconProps) => (
  <Svg size={size} className={className}>
    <path d="M12 8v5M12 16.5v.5" />
    <circle cx="12" cy="12" r="9.5" />
  </Svg>
);
